import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import moment from 'moment';

const WINDOW_MS   = 2 * 60 * 60 * 1000;
const BLOCK_W     = 0.62;
const BLOCK_H     = 0.62;
const BLOCK_D     = 0.62;
const STEM_H      = 0.75;
const TRON        = 0x049494;
const TRON_BRIGHT = 0x0ef8f8;
const CANVAS_H    = 180;
// Half-width of cube after Y rotation by π/5.2: (BLOCK_W/2)*(cos+sin) ≈ 0.431
// Slightly padded to 0.44 so the cube right edge never touches the NOW line
const CUBE_XHALF  = 0.44;

function blockToX(blockTime, xHalf) {
  const frac = Math.max(0, Math.min((Date.now() - blockTime) / WINDOW_MS, 1));
  return xHalf * (1 - 2 * frac);
}

export default function BlockTimeline3D({ blocks, lightning }) {
  const mountRef   = useRef(null);
  const stateRef   = useRef(null);
  const rafRef     = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);
  const [ticks, setTicks]     = useState({ marks: [], baselineY: null });
  useEffect(() => { tooltipRef.current = tooltip; }, [tooltip]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const W = mount.offsetWidth || 800;
    const H = CANVAS_H;

    const VIEW_H = 4.2;
    const VIEW_W = VIEW_H * (W / H);
    const X_HALF = VIEW_W / 2 - 12 * VIEW_H / H; // NOW line 2px from canvas right edge
    const camera = new THREE.OrthographicCamera(
      -VIEW_W / 2, VIEW_W / 2,
       VIEW_H / 2, -VIEW_H / 2,
      0.1, 300
    );
    camera.position.set(0, 5, 10);
    camera.lookAt(0, 1.0, 0);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(W, H);
    renderer.domElement.style.background = '#000';
    mount.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0x224444, 2.0));
    const key = new THREE.DirectionalLight(0x88dddd, 0.9);
    key.position.set(10, 20, 12);
    scene.add(key);
    const fill = new THREE.DirectionalLight(0x0ef8f8, 0.3);
    fill.position.set(-5, 5, 5);
    scene.add(fill);

    // Gray floor grid
    const grid = new THREE.GridHelper(X_HALF * 2 + 2, 30, 0x222222, 0x111111);
    grid.position.y = 0;
    scene.add(grid);

    // Gray baseline
    scene.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-X_HALF, 0.01, 0),
        new THREE.Vector3( X_HALF, 0.01, 0),
      ]),
      new THREE.LineBasicMaterial({ color: 0x555555 })
    ));

    // Gray edge markers (NOW on right, start on left) — full canvas height
    // Camera at (0,5,10) lookAt (0,1,0): world Y at canvas top (z=0) = 1 + VIEW_H/2 * sqrt(116)/10
    const edgeMat = new THREE.LineBasicMaterial({ color: 0x444444 });
    const edgeH   = 1 + VIEW_H / 2 * Math.sqrt(116) / 10;
    for (const ex of [X_HALF, -X_HALF]) {
      scene.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(ex, 0, 0),
          new THREE.Vector3(ex, edgeH, 0),
        ]),
        edgeMat
      ));
    }

    const meshes = {};
    stateRef.current = { scene, camera, renderer, meshes, animStart: performance.now(), W, H, X_HALF };

    // Force camera matrices so project() is correct before first render
    camera.updateMatrixWorld();

    // Compute initial tick positions immediately so labels don't flash at bottom
    const computeTicks = (cW) => {
      const st = stateRef.current;
      const xh = st ? st.X_HALF : X_HALF;
      const bProj = new THREE.Vector3(0, 0, 0).project(camera);
      const baselineY = (-bProj.y + 1) / 2 * H;
      const now = Date.now();
      const marks = [];
      for (let mins = 120; mins >= 0; mins -= 15) {
        const ts  = now - mins * 60 * 1000;
        const proj = new THREE.Vector3(blockToX(ts, xh), 0, 0).project(camera);
        marks.push({
          sx:      (proj.x + 1) / 2 * cW,
          label:   mins === 0 ? moment(ts).format('h:mm:ss a') : moment(ts).format('h:mm a'),
          isEnd:   mins === 0,
        });
      }
      setTicks({ marks, baselineY });
    };
    computeTicks(W);

    let mouseX = -9999, mouseY = -9999;
    const onMouseMove = e => {
      const r = mount.getBoundingClientRect();
      mouseX = e.clientX - r.left;
      mouseY = e.clientY - r.top;
    };
    const onMouseLeave = () => { mouseX = -9999; mouseY = -9999; setTooltip(null); };
    mount.addEventListener('mousemove', onMouseMove);
    mount.addEventListener('mouseleave', onMouseLeave);

    let lastTickUpdate = -Infinity;

    const animate = t => {
      rafRef.current = requestAnimationFrame(animate);
      if (document.hidden) return;
      const st = stateRef.current;
      if (!st) return;

      const ease = 1 - Math.pow(1 - Math.min((t - st.animStart) / 1000, 1), 3);
      const yLift = (1 - ease) * -8;

      const cW = mount.offsetWidth || W;
      let nearest = null, bestD = 40;

      for (const obj of Object.values(meshes)) {
        const x = blockToX(obj.blockData.time, st.X_HALF);
        obj.group.position.x = x;
        obj.group.position.y = yLift;

        const world = new THREE.Vector3(x, STEM_H + BLOCK_H * 0.5 + yLift, 0);
        const proj  = world.clone().project(st.camera);
        const sx = (proj.x + 1) / 2 * cW;
        const sy = (-proj.y + 1) / 2 * H;
        const d  = Math.hypot(sx - mouseX, sy - mouseY);
        if (d < bestD) { bestD = d; nearest = { sx, sy, blockData: obj.blockData }; }
      }

      const cur = tooltipRef.current;
      if ((nearest?.blockData ?? null) !== (cur?.blockData ?? null)) {
        setTooltip(nearest ? { x: nearest.sx, y: nearest.sy, blockData: nearest.blockData } : null);
      }

      // Recompute time-axis ticks every 10 s
      if (t - lastTickUpdate > 10000) {
        lastTickUpdate = t;
        computeTicks(cW);
      }

      st.renderer.render(st.scene, st.camera);
    };
    rafRef.current = requestAnimationFrame(animate);

    const onResize = () => {
      const st = stateRef.current;
      if (!st) return;
      const w = mount.offsetWidth;
      const vw = VIEW_H * (w / H);
      st.X_HALF        =  vw / 2 - 12 * VIEW_H / H;
      st.camera.left   = -vw / 2;
      st.camera.right  =  vw / 2;
      st.camera.top    =  VIEW_H / 2;
      st.camera.bottom = -VIEW_H / 2;
      st.camera.updateProjectionMatrix();
      st.renderer.setSize(w, H);
      computeTicks(w);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      mount.removeEventListener('mousemove', onMouseMove);
      mount.removeEventListener('mouseleave', onMouseLeave);
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose();
      stateRef.current = null;
    };
  }, []);

  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    const { scene, meshes } = st;

    const incoming = new Set(blocks.map(b => b.height));
    for (const h of Object.keys(meshes)) {
      if (!incoming.has(Number(h))) { scene.remove(meshes[h].group); delete meshes[h]; }
    }

    for (const block of blocks) {
      if (meshes[block.height]) continue;

      const group = new THREE.Group();
      group.position.set(blockToX(block.time, stateRef.current?.X_HALF ?? 14), 0, 0);

      // Dot on baseline
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.07, 8, 8),
        new THREE.MeshBasicMaterial({ color: TRON_BRIGHT })
      );
      dot.position.set(0, 0.01, 0);
      group.add(dot);

      // Stem: dot → block base
      group.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(0, 0.01, 0),
          new THREE.Vector3(0, STEM_H, 0),
        ]),
        new THREE.LineBasicMaterial({ color: TRON_BRIGHT, opacity: 0.65, transparent: true })
      ));

      // Glass cube — rotated 34° on Y so right face shows with centered camera
      const cubeGeo = new THREE.BoxGeometry(BLOCK_W, BLOCK_H, BLOCK_D);
      cubeGeo.rotateY(Math.PI / 5.2);
      cubeGeo.translate(0, BLOCK_H / 2 + STEM_H, 0);

      group.add(new THREE.Mesh(
        cubeGeo,
        new THREE.MeshLambertMaterial({
          color: new THREE.Color(TRON).multiplyScalar(1.2),
          transparent: true,
          opacity: 0.10,
          side: THREE.DoubleSide,
          depthWrite: false,
        })
      ));

      // Wireframe edges
      const rawEdge = new THREE.BoxGeometry(BLOCK_W, BLOCK_H, BLOCK_D);
      rawEdge.rotateY(Math.PI / 5.2);
      rawEdge.translate(0, BLOCK_H / 2 + STEM_H, 0);
      group.add(new THREE.LineSegments(
        new THREE.EdgesGeometry(rawEdge),
        new THREE.LineBasicMaterial({ color: TRON_BRIGHT })
      ));

      scene.add(group);
      meshes[block.height] = { group, blockData: block };
    }
  }, [blocks]);

  const tt = tooltip;
  const labelY = ticks.baselineY === null ? null : Math.min(ticks.baselineY + 4, CANVAS_H - 14);

  return (
    <div style={{ position: 'relative', width: '100%', background: '#000' }}>
      <div ref={mountRef} style={{ width: '100%', height: `${CANVAS_H}px`, display: 'block', background: '#000' }} />

      {/* Time-axis tick labels — only render once baselineY is known */}
      {labelY !== null && ticks.marks.map((tk, i) => (
        <div key={i} style={{
          position:      'absolute',
          left:           tk.isEnd ? undefined : tk.sx,
          right:          tk.isEnd ? 4 : undefined,
          top:            labelY,
          transform:      tk.isEnd ? 'none' : 'translateX(-50%)',
          fontSize:       9,
          fontFamily:    'monospace',
          color:          tk.isEnd ? '#888' : '#444',
          pointerEvents: 'none',
          whiteSpace:    'nowrap',
          userSelect:    'none',
        }}>
          {tk.label}
        </div>
      ))}

      {/* Hover tooltip */}
      {tt && (
        <div style={{
          position:      'absolute',
          left:           tt.x,
          top:            tt.y - 8,
          transform:     'translate(-50%, -100%)',
          background:    '#1a1a1a',
          border:        '1px solid #333',
          borderRadius:   2,
          padding:       '4px',
          fontSize:      '9px',
          fontFamily:    'monospace',
          color:         '#aaa',
          pointerEvents: 'none',
          zIndex:         200,
          width:          220,
          whiteSpace:    'normal',
          lineHeight:     1,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
            <span>Block <span style={{ color: '#0ef8f8' }}>#{tt.blockData.height.toLocaleString()}</span></span>
            <span style={{ color: '#666' }}>{moment(tt.blockData.time).format('hh:mm a')}</span>
          </div>
          {lightning && (<>
            <div style={{ borderTop: '1px solid #333', margin: '2px 0' }} />
            <div style={{ color: '#f7931a', fontSize: 8, fontWeight: 700, marginBottom: 2, letterSpacing: '0.8px' }}>
              ⚡ LIGHTNING NETWORK
            </div>
            <div style={{ display: 'flex', gap: 8, fontSize: 8, marginBottom: 2 }}>
              <span style={{ color: '#666' }}>Clearnet <span style={{ color: '#4CAF50' }}>{lightning.clearnet}</span></span>
              <span style={{ color: '#666' }}>Tor <span style={{ color: '#58a6ff' }}>{lightning.tor}</span></span>
              <span style={{ color: '#666' }}>Unk <span style={{ color: '#888' }}>{lightning.unknown}</span></span>
            </div>
            {lightning.isps?.length > 0 && (
              <div className="lightning-ticker-track">
                <div className="lightning-ticker-inner">
                  {[...lightning.isps, ...lightning.isps].map((isp, i) => (
                    <span key={i}>
                      <span style={{ color: '#555' }}>{(i % lightning.isps.length) + 1} </span>
                      <span style={{ color: '#ccc' }}>{isp.name} </span>
                      <span style={{ color: '#f7931a' }}>{isp.capacity_btc} BTC </span>
                      <span style={{ color: '#666' }}>{isp.nodes}n</span>
                      <span style={{ color: '#444' }}> · </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {lightning.countries?.length > 0 && (<>
              <div style={{ borderTop: '1px solid #333', margin: '2px 0' }} />
              <div style={{ color: '#f7931a', fontSize: 8, fontWeight: 700, marginBottom: 2, letterSpacing: '0.8px' }}>
                ₿ FULL NODES BY COUNTRY
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 8px' }}>
                {lightning.countries.slice(0, 6).map((c, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 8, lineHeight: 1 }}>
                    <span style={{ fontSize: 9 }}>{c.flag}</span>
                    <span style={{ color: '#999', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                    <span style={{ color: '#ccc', fontWeight: 700 }}>{c.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>)}
          </>)}
        </div>
      )}
    </div>
  );
}
